import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CloseIcon, SendIcon } from "@assets/icon";
import "./DislikeBlock.scss";

interface DislikeBlockProps {
  onCloseDislikeBlock: () => void;
  onSubmitReason: (reason: string) => void;
  onShowToast: () => void;
}

const DislikeBlock: React.FC<DislikeBlockProps> = ({
  onCloseDislikeBlock,
  onSubmitReason,
  onShowToast
}) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);
  
  const dislikePopupRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const { t } = useTranslation();
  
  const dislikeReasons = [
    t("common.dislikeReasonNotAccurate"),
    t("common.dislikeReasonNotMatch"),
    t("common.dislikeReasonTooLong"),
    t("common.dislikeReasonOther")
  ];
  
  const handleClickDislikeReason = (reason: string) => {
    if (reason !== t("common.dislikeReasonOther")) {
      onSubmitReason(reason);
      onShowToast();
      onCloseDislikeBlock();
      return;
    }
    
    setSelectedReason(reason);
  };
  
  const handleDislikeReasonSubmit = () => {
    if (!selectedReason) return;
    if (selectedReason === t("common.dislikeReasonOther") && !customReason.trim()) return;
    
    const finalReason = selectedReason === t("common.dislikeReasonOther") 
      ? customReason 
      : selectedReason;
    
    onSubmitReason(finalReason);
    onShowToast();
    onCloseDislikeBlock();
    
    setSelectedReason(null);
    setCustomReason("");
  };
  
  const handleClose = () => {
    setSelectedReason(null);
    setCustomReason("");
    setIsUserTyping(false);
    onShowToast();
    onCloseDislikeBlock();
  };
  
  useEffect(() => {
    if (!isUserTyping) {
      const timer = setTimeout(() => {
        onShowToast();
        onCloseDislikeBlock();
      }, 10000);
      
      return () => clearTimeout(timer);
    }
  }, [isUserTyping, onCloseDislikeBlock, onShowToast]);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dislikePopupRef.current && !dislikePopupRef.current.contains(event.target as Node)) {
        handleClose();
        onShowToast();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  useEffect(() => {
    if (dislikePopupRef.current) {
      setTimeout(() => {
        dislikePopupRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }, 100);
    }
  }, []);
  
  useEffect(() => {
    setSelectedReason(t("common.dislikeReasonOther"));
  }, [t]);
  
  return (
    <div className="chat-admin flex flex-col mt-[10px]">
      <div className="flex items-center">
        <div className="flex flex-col flex-1 pl-3">
          <div 
            ref={dislikePopupRef} 
            className="!w-full dislike-block border-chat-bot break-words rounded-lg p-3"
          >
            <div className="dislike-block-header-wrapper">
              <div className="dislike-block-header">
                {t("common.dislikePopupTitle")}
              </div>
              <button
                className="dislike-block-close"
                onClick={handleClose}
                aria-label="Đóng"
              >
                <CloseIcon />
              </button>
            </div>
            
            <div className="dislike-block-buttons">
              {dislikeReasons.map((reason) => (
                <button
                  key={reason}
                  className={`dislike-reason-btn ${selectedReason === reason ? 'selected' : ''}`}
                  onClick={() => handleClickDislikeReason(reason)}
                >
                  {reason}
                </button>
              ))}
            </div>
            
            {selectedReason === t("common.dislikeReasonOther") && (
              <div className="dislike-custom-input">
                <textarea
                  ref={textareaRef}
                  placeholder={t("common.dislikeCustomPlaceholder")}
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  onFocus={() => setIsUserTyping(true)}
                  onBlur={() => setIsUserTyping(false)}
                  className="custom-reason-textarea"
                  rows={3}
                  maxLength={256}
                />
                <div className="send-reason-wrapper">
                    <button
                        className={`send-reason-btn ${customReason.trim() ? "show-send-reason-btn": "hide-send-reason-btn"}`}
                        onClick={handleDislikeReasonSubmit}
                    >
                        <SendIcon />
                    </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DislikeBlock;